import { defineStore } from 'pinia';
import axios from 'axios';
import { useScenariosStore } from './scenarios';
import { useEventsStore } from './events';
import {getAPIURL} from '../logic/api';

const initialState = {
  currentDate: new Date(),
  selectedProfile: null,
  profile: null,
  calendarVersion: 0,
  calendarDays: []
};

export const useCalendarStore = defineStore('calendar', {
  state: () => ({ ...initialState }),

  getters: {
    // Helper function to get the correct amount for an event (monthly payment for loans, regular amount for others)
    getEventDisplayAmount() {
      return (event) => {
        const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
        if (loanCategories.includes(event.category) && event.monthly_payment && event.monthly_payment > 0) {
          // For mortgages, add escrow to the monthly payment
          if (event.category === 'MORTGAGE' && event.escrow && event.escrow > 0) {
            return parseFloat(event.monthly_payment) + parseFloat(event.escrow);
          }
          return event.monthly_payment;
        }
        return event.amount;
      };
    },
    
    // Get selected scenario from scenarios store
    selectedScenario() {
      const scenariosStore = useScenariosStore();
      return scenariosStore.selectedScenario;
    },
    
    // Get scenarios from scenarios store
    scenarios() {
      const scenariosStore = useScenariosStore();
      return scenariosStore.allScenarios;
    },
    
    // Get events from events store
    events() {
      const eventsStore = useEventsStore();
      return eventsStore.allEvents;
    },
    
    // Get events by month from events store
    eventsByMonth() {
      const eventsStore = useEventsStore();
      return eventsStore.monthlyEvents;
    },
    
    // Get cash flow totals from events store
    cashFlowInTotal() {
      const eventsStore = useEventsStore();
      return eventsStore.cashFlowInTotal;
    },
    
    cashFlowOutTotal() {
      const eventsStore = useEventsStore();
      return eventsStore.cashFlowOutTotal;
    },
    
    currentMonthYear: (state) => {
      return state.currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    },
    
    netFlow: (state) => {
      return state.calendarDaysCreditTotal - state.calendarDaysDebitTotal;
    },
    
    savings: (state) => {
      // TODO: Add a savings calculation here when backend is ready
      return (state.calendarDaysCreditTotal - state.calendarDaysDebitTotal) * 0;
    },
    
    calendarDaysDebitTotal: (state) => {
      let total = 0;
      
      // Calculate based on the actual calendar days that are displayed
      if (state.calendarDays && state.calendarDays.length > 0) {
        state.calendarDays.forEach(day => {
          if (day.events && day.events.length > 0 && day.currentMonth) {
            day.events.forEach(event => {
              if (event.type === 'DEBIT') {
                const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
                let amount;
                if (loanCategories.includes(event.category) && event.monthly_payment && event.monthly_payment > 0) {
                  amount = parseFloat(event.monthly_payment);
                  // For mortgages, add escrow to the amount
                  if (event.category === 'MORTGAGE' && event.escrow && event.escrow > 0) {
                    amount += parseFloat(event.escrow);
                  }
                } else {
                  amount = parseFloat(event.amount);
                }
                total += amount;
              }
            });
          }
        });
      }
      return total;
    },
    
    calendarDaysCreditTotal: (state) => {
      let total = 0;
      
      // Calculate based on the actual calendar days that are displayed
      if (state.calendarDays && state.calendarDays.length > 0) {
        state.calendarDays.forEach(day => {
          if (day.events && day.events.length > 0 && day.currentMonth) {
            day.events.forEach(event => {
              if (event.type === 'CREDIT') {
                const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
                let amount;
                if (loanCategories.includes(event.category) && event.monthly_payment && event.monthly_payment > 0) {
                  amount = parseFloat(event.monthly_payment);
                  // For mortgages, add escrow to the amount
                  if (event.category === 'MORTGAGE' && event.escrow && event.escrow > 0) {
                    amount += parseFloat(event.escrow);
                  }
                } else {
                  amount = parseFloat(event.amount);
                }
                total += amount;
              }
            });
          }
        });
      }
      return total;
    },

  },

  actions: {
    async fetchProfileInfo(profileID) {
      try {
        const url = getAPIURL()+'/api/profile/get-profile-by-id';
        const response = await axios.get(url, {
          params: { profileID }
        });
        this.profile = response.data;
        
        // Set the profile in the scenarios store
        const scenariosStore = useScenariosStore();
        scenariosStore.setProfile(this.profile);
        
        // Set the profile in the events store
        const eventsStore = useEventsStore();
        eventsStore.setProfile(this.profile);
      } catch (error) {
        console.error('Error fetching profile info:', error);
      }
    },



    updateCalendarDays() {
      const eventsStore = useEventsStore();
      const matchingEvents = eventsStore.monthlyEvents || [];
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const today = new Date();
      const days = [];

      const firstDayOfWeek = firstDay.getDay();
      const prevMonthLastDay = new Date(year, month, 0).getDate();

      // Filter to events that have at least one occurrence in the month.
      // If API returns no occurrences, fall back to start_date.
      const currentMonthEvents = matchingEvents.filter(eventData => {
        const occurrenceDates = (eventData.occurrences && Array.isArray(eventData.occurrences) && eventData.occurrences.length > 0)
          ? eventData.occurrences
          : [eventData.event?.start_date].filter(Boolean);

        return occurrenceDates.some(occurrence => {
          const tDateStr = (typeof occurrence === 'string') ? (occurrence.includes('T') ? occurrence.split('T')[0] : occurrence)
                                                           : new Date(occurrence).toISOString().split('T')[0];
          const [tYear, tMonth] = tDateStr.split('-').map(Number);
          return tYear === year && (tMonth - 1) === month;
        });
      });

      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const dayNum = prevMonthLastDay - i;
        const day = new Date(year, month - 1, dayNum);
        let events = [];
        for (let e of currentMonthEvents) {
          if (!e.occurrences || !Array.isArray(e.occurrences)) {
            continue;
          }
          let occurrences = (e.occurrences && Array.isArray(e.occurrences) && e.occurrences.length > 0)
            ? e.occurrences
            : [e.event.start_date].filter(Boolean);
          for (let o of occurrences) {
            const occurrenceDateStr = (typeof o === 'string') ? (o.includes('T') ? o.split('T')[0] : o) : new Date(o).toISOString().split('T')[0];
            const dayDateStr = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
            if (occurrenceDateStr === dayDateStr) {
              // Use monthly payment for loan categories, regular amount for others
              const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
              let displayAmount;
              if (loanCategories.includes(e.event.category) && e.event.monthly_payment && e.event.monthly_payment > 0) {
                // For mortgages, add escrow to the monthly payment
                if (e.event.category === 'MORTGAGE' && e.event.escrow && e.event.escrow > 0) {
                  displayAmount = parseFloat(e.event.monthly_payment) + parseFloat(e.event.escrow);
                } else {
                  displayAmount = e.event.monthly_payment;
                }
              } else {
                displayAmount = e.event.amount;
              }
              
              events.push({
                id: e.event.id,
                name: e.event.name,
                amount: displayAmount,
                type: e.event.type,
                startDate: e.event.start_date,
                endDate: e.event.end_date,
                frequency: e.event.frequency,
                category: e.event.category,
                description: e.event.description, 
                active: e.event.active,
                profileID: e.event.profile_id,
                scenarioID: e.event.scenario_id, 
                occurrences: e.occurrences,
              });
            }
          }
        }
        days.push({
          day: dayNum,
          currentMonth: false,
          date: day,
          hasEvents: events.length > 0,
          events,
          isToday: false
        });
      }

              for (let i = 1; i <= lastDay.getDate(); i++) {
        const day = new Date(year, month, i);
        day.setHours(0, 0, 0, 0);
        let events = [];
        for (let e of currentMonthEvents) {
          if (!e.occurrences || !Array.isArray(e.occurrences)) {
            continue;
          }
          let occurrences = (e.occurrences && Array.isArray(e.occurrences) && e.occurrences.length > 0)
            ? e.occurrences
            : [e.event.start_date].filter(Boolean);
          for (let o of occurrences) {
            const occurrenceDateStr = (typeof o === 'string') ? (o.includes('T') ? o.split('T')[0] : o) : new Date(o).toISOString().split('T')[0];
            const dayDateStr = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
            if (occurrenceDateStr === dayDateStr) {
              // Use monthly payment for loan categories, regular amount for others
              const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
              let displayAmount;
              if (loanCategories.includes(e.event.category) && e.event.monthly_payment && e.event.monthly_payment > 0) {
                // For mortgages, add escrow to the monthly payment
                if (e.event.category === 'MORTGAGE' && e.event.escrow && e.event.escrow > 0) {
                  displayAmount = parseFloat(e.event.monthly_payment) + parseFloat(e.event.escrow);
                } else {
                  displayAmount = e.event.monthly_payment;
                }
              } else {
                displayAmount = e.event.amount;
              }
              
              events.push({
                id: e.event.id,
                name: e.event.name,
                amount: displayAmount,
                type: e.event.type,
                startDate: e.event.start_date,
                endDate: e.event.end_date,
                frequency: e.event.frequency,
                category: e.event.category,
                description: e.event.description,
                active: e.event.active,
                profileID: e.event.profile_id,
                scenarioID: e.event.scenario_id,
                occurrences: e.occurrences,
              });
            }
          }
        }

        days.push({
          day: i,
          currentMonth: true,
          date: day,
          hasEvents: events.length > 0,
          events,
          isToday: day.getDate() === today.getDate() &&
            day.getMonth() === today.getMonth() &&
            day.getFullYear() === today.getFullYear()
        });
      }

      const lastDayOfWeek = lastDay.getDay();
      const daysToAdd = 6 - lastDayOfWeek;

      for (let i = 1; i <= daysToAdd; i++) {
        const day = new Date(year, month + 1, i);
        let events = [];
        for (let e of currentMonthEvents) {
          if (!e.occurrences || !Array.isArray(e.occurrences)) {
            continue;
          }
          let occurrences = (e.occurrences && Array.isArray(e.occurrences) && e.occurrences.length > 0)
            ? e.occurrences
            : [e.event.start_date].filter(Boolean);
          for (let o of occurrences) {
            const occurrenceDateStr = (typeof o === 'string') ? (o.includes('T') ? o.split('T')[0] : o) : new Date(o).toISOString().split('T')[0];
            const dayDateStr = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
            if (occurrenceDateStr === dayDateStr) {
              // Use monthly payment for loan categories, regular amount for others
              const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
              let displayAmount;
              if (loanCategories.includes(e.event.category) && e.event.monthly_payment && e.event.monthly_payment > 0) {
                // For mortgages, add escrow to the monthly payment
                if (e.event.category === 'MORTGAGE' && e.event.escrow && e.event.escrow > 0) {
                  displayAmount = parseFloat(e.event.monthly_payment) + parseFloat(e.event.escrow);
                } else {
                  displayAmount = e.event.monthly_payment;
                }
              } else {
                displayAmount = e.event.amount;
              }
              
              events.push({
                id: e.event.id,
                name: e.event.name,
                amount: displayAmount,
                type: e.event.type,
                startDate: e.event.start_date,
                endDate: e.event.end_date,
                frequency: e.event.frequency,
                category: e.event.category,
                description: e.event.description,
                active: e.event.active,
                profileID: e.event.profile_id,
                scenarioID: e.event.scenario_id,
                occurrences: e.occurrences,
              });
            }
          }
        }
        days.push({
          day: i,
          currentMonth: false,
          date: day,
          hasEvents: events.length > 0,
          events,
          isToday: false
        });
      }

      // Debug logging for totals
      let totalDebit = 0;
      let totalCredit = 0;
      if (days && days.length > 0) {
        days.forEach(day => {
          if (day.currentMonth && day.events && day.events.length > 0) {
            day.events.forEach(event => {
              if (event.type === 'DEBIT') {
                const amount = parseFloat(event.amount);
                totalDebit += amount;
              } else if (event.type === 'CREDIT') {
                const amount = parseFloat(event.amount);
                totalCredit += amount;
              }
            });
          }
        });
      }

      this.calendarDays = days;
    },

    previousMonth() {
      const newDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
      this.currentDate = newDate;
      this.calendarVersion++;
      
      // Update current date in events store
      const eventsStore = useEventsStore();
      eventsStore.setCurrentDate(this.currentDate);
      
      // Fetch events for the new month
      eventsStore.fetchEventsForMonthByScenario();
      eventsStore.fetchEvents();
      
      // Update calendar days
      this.updateCalendarDays();
    },

    nextMonth() {
      const newDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
      this.currentDate = newDate;
      this.calendarVersion++;
      
      // Update current date in events store
      const eventsStore = useEventsStore();
      eventsStore.setCurrentDate(this.currentDate);
      
      // Fetch events for the new month
      eventsStore.fetchEventsForMonthByScenario();
      eventsStore.fetchEvents();
      
      // Update calendar days
      this.updateCalendarDays();
    },



    resetCalendar() {
      // Create a new date object to ensure it's fresh
      const newState = {
        ...initialState,
        currentDate: new Date(),
        calendarDays: []
      };
      
      // Update the state
      Object.assign(this, newState);
    },

    resetForNewUser() {
      try {
        // Reset the calendar store for a new user login
        this.resetCalendar();
        this.profile = null;
        this.calendarDays = [];
        
        // Reset the scenarios store
        const scenariosStore = useScenariosStore();
        scenariosStore.resetForNewUser();
        
        // Reset the events store
        const eventsStore = useEventsStore();
        eventsStore.resetForNewUser();
      } catch (error) {
        console.error('Error resetting stores for new user:', error);
      }
    },

    async setCalendarToDate(year, month, day = 1) {
      const newDate = new Date(year, month, day);
      this.currentDate = newDate;
      this.calendarVersion++;
      
      // Update current date in events store
      const eventsStore = useEventsStore();
      eventsStore.setCurrentDate(this.currentDate);
      
      // Fetch events for the new date
      await eventsStore.fetchEventsForMonthByScenario();
      await eventsStore.fetchEvents();
      
      // Update calendar days
      this.updateCalendarDays();
    },


  }
});